import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { APP_CONFIG } from './config';
import App from './App.tsx';
import './index.css';
import './shared/i18n/i18n';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const shouldEnableSentryDebugMode = import.meta.env.VITE_SENTRY_DEBUG === 'true';
const appEnvironment = import.meta.env.VITE_APP_ENV || import.meta.env.MODE;

const sentryAppErrorFallback: Sentry.FallbackRender = ({ resetError }) => (
  <div
    className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-4 p-6 transition-colors"
    data-testid="sentry-root-error-fallback"
  >
    <p className="text-center text-title max-w-md">
      Ha ocurrido un error inesperado. Puedes reintentar o recargar la página.
    </p>
    <div className="flex flex-wrap gap-3 justify-center">
      <button
        type="button"
        className="rounded-md bg-primary px-4 py-2 text-btn-text text-sm font-medium transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        onClick={() => resetError()}
      >
        Reintentar
      </button>
      <button
        type="button"
        className="rounded-md border border-lines bg-card-bg px-4 py-2 text-sm font-medium text-title transition-colors hover:bg-hover-press focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        onClick={() => window.location.reload()}
      >
        Recargar página
      </button>
    </div>
  </div>
);

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: appEnvironment,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.browserProfilingIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    profilesSampleRate: 1.0,
    enableLogs: true,
    enableMetrics: true,
    enabled: import.meta.env.PROD || shouldEnableSentryDebugMode,
    beforeSend(event) {
      if (typeof window === 'undefined') {
        return event;
      }
      const { pathname, search, hash, href } = window.location;
      event.tags = { ...event.tags, pathname };
      event.contexts = {
        ...event.contexts,
        route: {
          pathname,
          search,
          hash,
          href,
        },
      };
      return event;
    },
  });

  // En modo debug enviamos un smoke test para validar la recepción de eventos.
  if (shouldEnableSentryDebugMode) {
    const smokeTestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const smokeMessage = `[smoke:${smokeTestId}] [env:${appEnvironment}] Smoke test Beeping`;

    try {
      Sentry.captureException(new Error(smokeMessage));
      console.log('🔍 Sentry Debug - Smoke test enviado', {
        sent: true,
        smokeTestId,
        environment: appEnvironment,
        message: smokeMessage,
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';

      console.log('🔍 Sentry Debug - Error enviando smoke test', {
        sent: false,
        smokeTestId,
        environment: appEnvironment,
        message: smokeMessage,
        errorMessage,
      });
    }
  } else {
    console.log('🔍 Sentry Debug - Smoke test no enviado: VITE_SENTRY_DEBUG no es "true"', {
      sent: false,
      environment: appEnvironment,
      sentryDebugValue: import.meta.env.VITE_SENTRY_DEBUG,
    });
  }
} else if (shouldEnableSentryDebugMode) {
  console.log('🔍 Sentry Debug - Smoke test no enviado: falta VITE_SENTRY_DSN', {
    sent: false,
    environment: appEnvironment,
  });
}

// Establecer título inicial desde la configuración
document.title = APP_CONFIG.appName;

const appTree = (
  <StrictMode>
    <App />
  </StrictMode>
);

createRoot(document.getElementById('root')!).render(
  sentryDsn ? (
    <Sentry.ErrorBoundary
      fallback={sentryAppErrorFallback}
      beforeCapture={(scope) => {
        scope.setTag('error_boundary', 'root');
      }}
    >
      {appTree}
    </Sentry.ErrorBoundary>
  ) : (
    appTree
  ),
);
